import { useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { Box, Button, FormControl, Input, InputAdornment, InputLabel, Menu, MenuItem, Select, Typography } from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ClearIcon from '@mui/icons-material/Clear';

const CatalogFilterBar = (props) => {
  const { theme } = useTheme();

  const [anchorEl, setAnchorEl] = useState("");
  const open = Boolean(anchorEl);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      // If Enter key is pressed, apply the search filter
      e.preventDefault();
      // Filter is already applied through useEffect
      props.handleSearchByQuery(props.searchQuery);
    }
  };

  const handleSelectFilterType = (type) => {
    props.setSearchFilterType(type);
    setAnchorEl(null);
  }

  const getChipColor = (status) => {
    if (status === "PUBLISHED" || status === "ACTIVE") {
      return theme.accent.success;
    } else if (status === "ERROR" || status === "RETIRED" || status === "SYSTEM_PROBLEM") {
      return theme.accent.error;
    } else {
      return theme.accent.warning;
    }
  };

  const styles = {
    filterBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '15px',
      backgroundColor: theme.background.secondary,
      borderRadius: '5px',
      marginBottom: '15px',
      boxShadow: `0 2px 4px rgba(0,0,0,0.1)`,
    },
    searchInput: {
      minWidth: 200,
      marginRight: '15px',
      '& .MuiInputBase-root': {
        borderRadius: '4px',
        backgroundColor: theme.background.secondary,
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
        border: `1px solid ${theme.border.medium}`,
        padding: '4px 8px',
        '&:hover': {
          borderColor: theme.border.dark,
        },
        '&.Mui-focused': {
          borderColor: theme.color.primary,
        },
      },
      '& .MuiInputAdornment-root': {
        color: theme.text.primary,
      },
      '& .MuiInputBase-input': {
        padding: '6px 4px',
      },
    },
    formControl: {
      minWidth: 150,
      marginRight: '15px',
    },
    inputLabel: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      '&.Mui-focused': {
        color: theme.color.primary,
      },
    },
    select: {
      backgroundColor: theme.background.secondary,
      '& .MuiSelect-select': {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
        minWidth: '150px',
      },
      '& .MuiSelect-icon': {
        color: theme.text.primary,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.border.medium, // Set border color
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.border.medium, // Set border color on hover
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.border.medium, // Set border color when focused
      },
    },
    menuItem: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      '&.Mui-selected': {
        backgroundColor: `${theme.background.primary}`,
        '&:hover': {
          backgroundColor: `${theme.background.secondary}`,
        },
      },
      '&:hover': {
        backgroundColor: `${theme.background.secondary}`,
      }
    },
    chip: {
      fontFamily: theme.fontFamily,
      color: '#2C2C2C',
      borderRadius: '20px',
      width: 'fit-content',
      px: '10px',
      fontSize: '14px'
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      borderRadius: '20px',
      border: `1px solid ${theme.border.light}`,
      backgroundColor: theme.accent.warning,
      color: theme.text.primary,
      ml: 2
    },
  };

  return (
    <Box mx={2}>
      <Box sx={styles.filterBar}>
        <Box display="flex" alignItems="center">
          <FilterListIcon sx={{ color: theme.text.primary, mr: '10px' }} />

          {/* Search Box */}
          <Box sx={styles.searchInput}>
            <Input
              placeholder={`Search by ${props.searchFilterType}...`}
              value={props.searchQuery}
              onChange={(e) => props.setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              fullWidth
              sx={{
                '&:before': { borderBottomColor: 'transparent' },
                '&:after': { borderBottomColor: theme.color.primary },
                '&:hover:before': { borderBottomColor: theme.border.medium + '!important' },
              }}
              endAdornment={
                <InputAdornment position="end">
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', }}
                    id="IFSearchFilterTypeTypography"
                    aria-controls={open ? 'IFSearchFilterTypeSelect' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                  >
                    <ArrowDropDownOutlinedIcon sx={{ color: theme.text.primary, }} />
                  </Box>
                  <Menu
                    id="IFSearchFilterTypeSelect"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{
                      'aria-labelledby': 'IFSearchFilterTypeTypography',
                    }}
                    PaperProps={{
                      sx: {
                        backgroundColor: theme.background.primary,
                        color: theme.text.primary,
                      },
                    }}
                  >
                    {["SKU", "Item ID"].map((type) => { // , "Barcode"
                      return (
                        <MenuItem key={type} selected={type === props.searchFilterType} onClick={() => handleSelectFilterType(type)} sx={styles.menuItem}>
                          {type}
                        </MenuItem>
                      )
                    })}
                  </Menu>
                </InputAdornment>
              }
            />
          </Box>

          <FormControl variant="outlined" sx={styles.formControl} size="small">
            <InputLabel sx={styles.inputLabel} id={"select-label-publish-status"}>Status</InputLabel>
            <Select
              labelId={"select-label-publish-status"}
              label={"Published Status"}
              value={props.publishedStatus}
              onChange={(e) => { props.setPublishedStatus(e.target.value); props.handleSearchByPublishedStatus(e.target.value); }}
              sx={styles.select}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: theme.background.primary,
                  },
                },
              }}
            >
              <MenuItem value="">
                <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  <em>All</em>
                </Typography>
              </MenuItem>
              {["PUBLISHED", "UNPUBLISHED"].map((status) => (
                <MenuItem key={status} value={status} >
                  <Typography sx={{ ...styles.chip, backgroundColor: getChipColor(status), }}>
                    {status}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" sx={styles.formControl} size="small">
            <InputLabel sx={styles.inputLabel} id={"select-label-batch"}>Batch</InputLabel>
            <Select
              labelId={"select-label-batch"}
              label={"Batch"}
              value={props.batchFilter}
              onChange={(e) => { props.setBatchFilter(e.target.value); props.handleSearchByBatch(e.target.value) }}
              sx={styles.select}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: theme.background.primary,
                    maxHeight: 300, // Set your desired max height here (in pixels)
                    overflowY: 'auto'
                  },
                },
              }}
            >
              <MenuItem value="">
                <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  <em>All</em>
                </Typography>
              </MenuItem>
              {props.batches.map((batch, index) => (
                <MenuItem key={index} value={batch}>
                  <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                    {batch}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {((props.batchFilter && props.batchFilter !== "none") || (props.searchQuery)) && (
            <Button
              variant="contained"
              startIcon={<ClearIcon />}
              onClick={props.resetFilters}
              sx={{
                ...styles.button,

              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {props.totalItems > 0 && (
          <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>
            {props.totalItems} item{props.totalItems !== 1 ? 's' : ''} in inventory
          </Typography>
        )}
      </Box>
    </Box>
  )
}
export default CatalogFilterBar;